import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useMemo } from "react";

import { onUnauthenticated } from "./api/fetcher";
import { getMeQueryKey, useLogin, useLogout } from "./api/generated/stockvol";
import type { Me } from "./api/generated/model";
import { useMeInfo } from "./api/hooks";

export type Role = "viewer" | "operator" | "admin";
const RANK: Record<Role, number> = { viewer: 0, operator: 1, admin: 2 };

export function atLeast(role: Role | undefined, min: Role): boolean {
  return role !== undefined && RANK[role] >= RANK[min];
}

interface Auth {
  me: Me | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginError: unknown;
  loggingIn: boolean;
}

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useMeInfo();
  const loginM = useLogin();
  const logoutM = useLogout();

  useEffect(() => {
    // any 401: the session is gone — forget everything and show the login page
    onUnauthenticated(() => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== getMeQueryKey()[0] });
      qc.setQueryData(getMeQueryKey(), undefined);
      void qc.invalidateQueries({ queryKey: getMeQueryKey() });
    });
    return () => onUnauthenticated(null);
  }, [qc]);

  const value = useMemo<Auth>(
    () => ({
      me: meQuery.isSuccess ? meQuery.data : null,
      loading: meQuery.isPending,
      loginError: loginM.error,
      loggingIn: loginM.isPending,
      login: async (username, password) => {
        await loginM.mutateAsync({ data: { username, password } });
        qc.clear();
        await qc.invalidateQueries({ queryKey: getMeQueryKey() });
      },
      logout: async () => {
        try {
          await logoutM.mutateAsync();
        } finally {
          qc.clear();
          await qc.invalidateQueries({ queryKey: getMeQueryKey() });
        }
      },
    }),
    [meQuery.isSuccess, meQuery.data, meQuery.isPending, loginM, logoutM, qc],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const a = useContext(AuthContext);
  if (a === null) throw new Error("useAuth outside <AuthProvider>");
  return a;
}

/** Renders its children only for `min` and above (a viewer sees no controls). */
export function RequireRole({ min, children }: { min: Role; children: ReactNode }) {
  const { me } = useAuth();
  return atLeast(me?.role, min) ? <>{children}</> : null;
}
